import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { StandardPageSkeleton } from '../../components/common/Skeleton';
import { PageFadeSection } from '../../components/common/PageFadeSection';
import { UserRole } from '../../constants/userRole';
import { 
  RefreshCw, 
  CheckCircle, 
  AlertTriangle, 
  Clock,
  Database,
  Server,
  ArrowRight,
  Zap,
  History,
  Settings,
  Play,
  Pause,
  Trash2,
  RotateCcw,
  XCircle,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import api from '../../api/axiosNew';
import { getFailedEntries, retrySingleSync, deleteFailedEntry } from '../../api/syncNew.api';
import { parseUTC } from '../../utils/dateUtils';

const SyncPage = () => {
  const { t } = useTranslation();
  const { getAccessToken, user } = useAuth();
  const isAdmin = user?.role === UserRole.Admin;
  const isLead = user?.role === UserRole.Lead || user?.role === UserRole.PM;
  const [syncStatus, setSyncStatus] = useState(null);
  const [syncHistory, setSyncHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [autoSync, setAutoSync] = useState(false);
  const [failedItems, setFailedItems] = useState([]);
  const [failedLoading, setFailedLoading] = useState(false);
  const [showFailed, setShowFailed] = useState(true);
  const [retryingId, setRetryingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const unwrapApiData = (value) => {
    if (value == null) return value;
    if (Array.isArray(value)) return value;
    return value.data ?? value.Data ?? value.items ?? value.Items ?? value.values ?? value.Values ?? value.$values ?? value;
  };

  const unwrapApiList = (value) => {
    const first = unwrapApiData(value);
    const second = unwrapApiData(first);
    if (Array.isArray(second)) return second;
    if (Array.isArray(second?.$values)) return second.$values;
    if (Array.isArray(second?.items)) return second.items;
    if (Array.isArray(second?.Items)) return second.Items;
    return [];
  };
    const formatSyncTimeAgo = useCallback(
        (timestamp) => {
            if (!timestamp) return '';
            const diff = Date.now() - new Date(timestamp).getTime();
            const mins = Math.floor(diff / 60000);
            if (mins < 1) return t('pages.sync.justNow');
            if (mins < 60) return t('pages.sync.minAgo', { n: mins });
            const hours = Math.floor(mins / 60);
            if (hours < 24) {
                return hours === 1 ? t('pages.sync.oneHourAgo') : t('pages.sync.nHoursAgo', { n: hours });
            }
            return parseUTC(timestamp).toLocaleString();
        },
        [t]
    );
  useEffect(() => {
    fetchSyncData();
    fetchFailedItems();
    // Auto-refresh every 30 seconds
    const interval = setInterval(() => {
      fetchSyncData();
      fetchFailedItems();
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchSyncData = async () => {
    try {
      const token = await getAccessToken();
      const headers = { Authorization: `Bearer ${token}` };
      
      // Backend automatically scopes by userId from JWT token
      const [statusRes, historyRes] = await Promise.all([
        api.get('/api/sync/status', { headers }),
        api.get('/api/sync/history?limit=10', { headers })
      ]);
      
      const statusData = unwrapApiData(statusRes.data);
      const historyItems = unwrapApiList(historyRes.data);

      setSyncStatus({
        ...statusData,
        // compute percentage if not provided
        syncPercentage: (() => {
          const d = statusData;
          if (d?.totalEntries > 0) return (d.syncedEntries / d.totalEntries) * 100;
          return 0;
        })()
      });
      setSyncHistory(historyItems);
    } catch (error) {
      console.error('Failed to fetch sync data:', error);
      // Use demo data
      setSyncStatus({
        totalEntries: 128,
        syncedEntries: 108,
        pendingEntries: 15,
        failedEntries: 5,
        syncPercentage: 84.4,
        lastSyncTime: new Date().toISOString(),
        isConnected: true
      });
    } finally {
      setLoading(false);
    }
  };

  const handleTriggerSync = async () => {
    setSyncing(true);
    try {
      const token = await getAccessToken();
      await api.post('/api/sync/trigger', {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchSyncData();
    } catch (error) {
      console.error('Sync failed:', error);
    } finally {
      setSyncing(false);
    }
  };

  const handleRetryFailed = async () => {
    setSyncing(true);
    try {
      const token = await getAccessToken();
      await api.post('/api/sync/retry-failed', {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchSyncData();
      await fetchFailedItems();
    } catch (error) {
      console.error('Retry failed:', error);
    } finally {
      setSyncing(false);
    }
  };

  const fetchFailedItems = async () => {
    try {
      setFailedLoading(true);
      // Backend automatically scopes by userId from JWT token
      const result = await getFailedEntries();
      setFailedItems(unwrapApiList(result));
    } catch (error) {
      console.error('Failed to fetch failed entries:', error);
      setFailedItems([]);
    } finally {
      setFailedLoading(false);
    }
  };

  const handleRetrySingle = async (id, type) => {
    setRetryingId(id);
    try {
      await retrySingleSync(id, type);
      await fetchSyncData();
      await fetchFailedItems();
    } catch (error) {
      console.error('Retry single failed:', error);
    } finally {
      setRetryingId(null);
    }
  };

  const handleDeleteFailed = async (id, type) => {
    setDeletingId(id);
    try {
      await deleteFailedEntry(id, type);
      await fetchSyncData();
      await fetchFailedItems();
    } catch (error) {
      console.error('Delete failed entry error:', error);
    } finally {
      setDeletingId(null);
    }
  };

  const StatusCard = ({ title, value, icon: Icon, color, description }) => (
    <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-sm border border-gray-100 dark:border-gray-700">
      <div className="flex items-center justify-between mb-4">
        <div className={`p-3 rounded-xl ${color}`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
        <span className="text-3xl font-bold text-gray-900 dark:text-gray-100">{value}</span>
      </div>
      <h3 className="font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
      {description && <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{description}</p>}
    </div>
  );

  if (loading) {
    return <StandardPageSkeleton />;
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <PageFadeSection>
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            {isAdmin ? t('pages.sync.titleAdmin') : t('pages.sync.titleUser')}
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            {isAdmin ? t('pages.sync.subAdmin') : t('pages.sync.subUser')}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* {(isAdmin || isLead) && (
            <button
              onClick={handleRetryFailed}
              disabled={syncing || !syncStatus?.failedEntries}
              className="flex items-center gap-2 px-4 py-2.5 border border-orange-300 dark:border-orange-700 text-orange-600 dark:text-orange-400 rounded-xl font-medium hover:bg-orange-50 dark:hover:bg-orange-900/20 transition-all disabled:opacity-50"
            >
              <AlertTriangle className="w-5 h-5" />
              Retry Failed ({syncStatus?.failedEntries || 0})
            </button>
          )} */}
          {/* <button
            onClick={handleTriggerSync}
            disabled={syncing}
            className="flex items-center gap-2 px-4 py-2.5 bg-operis-blue hover:bg-primary-700 text-white rounded-xl font-medium transition-all shadow-sm hover:shadow-md disabled:opacity-50"
          >
            <RefreshCw className={`w-5 h-5 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? 'Syncing...' : 'Sync Now'}
          </button> */}
        </div>
      </div>
      </PageFadeSection>

      <PageFadeSection>
      {/* Connection Status Banner */}
      <div className={`p-4 rounded-2xl flex items-center gap-4 ${
        syncStatus?.isConnected 
          ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800' 
          : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
      }`}>
        <div className={`p-3 rounded-xl ${syncStatus?.isConnected ? 'bg-green-500' : 'bg-red-500'}`}>
          <Server className="w-6 h-6 text-white" />
        </div>
        <div className="flex-1">
          <h3 className={`font-semibold ${syncStatus?.isConnected ? 'text-green-700 dark:text-green-400' : 'text-red-700 dark:text-red-400'}`}>
            {syncStatus?.isConnected ? t('pages.sync.connected') : t('pages.sync.disconnected')}
          </h3>
          <p className={`text-sm ${syncStatus?.isConnected ? 'text-green-600 dark:text-green-500' : 'text-red-600 dark:text-red-500'}`}>
            {syncStatus?.isConnected 
              ? t('pages.sync.lastSync', {
                  when: syncStatus?.lastSyncTime
                    ? parseUTC(syncStatus.lastSyncTime).toLocaleString()
                    : t('pages.sync.never'),
                })
              : t('pages.sync.checkNetwork')
            }
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className={`w-3 h-3 rounded-full ${syncStatus?.isConnected ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`}></span>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatusCard
          title={t('pages.sync.totalEntries')}
          value={syncStatus?.totalEntries || 0}
          icon={Database}
          color="bg-gradient-to-br from-blue-500 to-blue-600"
          description={isAdmin ? t('pages.sync.descTotalAdmin') : t('pages.sync.descTotalUser')}
        />
        <StatusCard
          title={t('pages.sync.synced')}
          value={syncStatus?.syncedEntries || 0}
          icon={CheckCircle}
          color="bg-gradient-to-br from-green-500 to-green-600"
          description={isAdmin ? t('pages.sync.descSyncedAdmin') : t('pages.sync.descSyncedUser')}
        />
        <StatusCard
          title={t('pages.sync.pending')}
          value={syncStatus?.pendingEntries || 0}
          icon={Clock}
          color="bg-gradient-to-br from-yellow-500 to-yellow-600"
          description={isAdmin ? t('pages.sync.descPendingAdmin') : t('pages.sync.descPendingUser')}
        />
        <StatusCard
          title={t('pages.sync.failed')}
          value={syncStatus?.failedEntries || 0}
          icon={AlertTriangle}
          color="bg-gradient-to-br from-red-500 to-red-600"
          description={isAdmin ? t('pages.sync.descFailedAdmin') : t('pages.sync.descFailedUser')}
        />
      </div>

      {/* Sync Progress */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-sm border border-gray-100 dark:border-gray-700">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{t('pages.sync.syncProgress')}</h2>
          <span className="text-2xl font-bold text-operis-blue dark:text-blue-400">
            {syncStatus?.syncPercentage?.toFixed(1) || 0}%
          </span>
        </div>
        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-4 mb-4">
          <div 
            className="bg-gradient-to-r from-operis-blue to-primary-600 h-4 rounded-full transition-all duration-500"
            style={{ width: `${syncStatus?.syncPercentage || 0}%` }}
          ></div>
        </div>
        <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400">
          <span>0%</span>
          <span>{t('pages.sync.syncedToSap')}</span>
          <span>100%</span>
        </div>
      </div>

      {/* Auto Sync Settings - Admin/Lead only */}
      {(isAdmin || isLead) && (
      <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-sm border border-gray-100 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-xl">
              <Settings className="w-6 h-6 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-gray-100">{t('pages.sync.autoSyncTitle')}</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">{t('pages.sync.autoSyncSub')}</p>
            </div>
          </div>
          <button
            onClick={() => setAutoSync(!autoSync)}
            className={`relative w-14 h-7 rounded-full transition-colors ${
              autoSync ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-600'
            }`}
          >
            <span className={`absolute top-1 w-5 h-5 bg-white rounded-full transition-transform ${
              autoSync ? 'translate-x-8' : 'translate-x-1'
            }`}></span>
          </button>
        </div>
      </div>
      )}

      {/* Sync Flow Diagram - Admin/Lead only */}
      {(isAdmin || isLead) && (
      <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-sm border border-gray-100 dark:border-gray-700">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-6">{t('pages.sync.syncFlow')}</h2>
        <div className="flex items-center justify-between max-w-3xl mx-auto">
          <div className="flex flex-col items-center">
            <div className="p-4 bg-blue-100 dark:bg-blue-900/30 rounded-2xl mb-3">
              <Database className="w-8 h-8 text-blue-600 dark:text-blue-400" />
            </div>
            <span className="font-medium text-gray-900 dark:text-gray-100">{t('pages.sync.localDatabase')}</span>
            <span className="text-sm text-gray-500 dark:text-gray-400">{t('pages.sync.mssql')}</span>
          </div>
          
          <div className="flex-1 flex items-center justify-center">
            <div className="flex items-center gap-2">
              <div className="w-12 h-0.5 bg-gray-300 dark:bg-gray-600"></div>
              <ArrowRight className="w-6 h-6 text-operis-blue dark:text-blue-400" />
              <div className="w-12 h-0.5 bg-gray-300 dark:bg-gray-600"></div>
            </div>
          </div>
          
          <div className="flex flex-col items-center">
            <div className="p-4 bg-yellow-100 dark:bg-yellow-900/30 rounded-2xl mb-3">
              <Zap className="w-8 h-8 text-yellow-600 dark:text-yellow-400" />
            </div>
            <span className="font-medium text-gray-900 dark:text-gray-100">{t('pages.sync.syncService')}</span>
            <span className="text-sm text-gray-500 dark:text-gray-400">{t('pages.sync.wsdlSoap')}</span>
          </div>
          
          <div className="flex-1 flex items-center justify-center">
            <div className="flex items-center gap-2">
              <div className="w-12 h-0.5 bg-gray-300 dark:bg-gray-600"></div>
              <ArrowRight className="w-6 h-6 text-operis-blue dark:text-blue-400" />
              <div className="w-12 h-0.5 bg-gray-300 dark:bg-gray-600"></div>
            </div>
          </div>
          
          <div className="flex flex-col items-center">
            <div className="p-4 bg-green-100 dark:bg-green-900/30 rounded-2xl mb-3">
              <Server className="w-8 h-8 text-green-600 dark:text-green-400" />
            </div>
            <span className="font-medium text-gray-900 dark:text-gray-100">{t('pages.sync.sapByDesign')}</span>
            <span className="text-sm text-gray-500 dark:text-gray-400">{t('pages.sync.cloudErp')}</span>
          </div>
        </div>
      </div>
      )}

      </PageFadeSection>

      <PageFadeSection>
      {/* Failed Entries Detail */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-red-200 dark:border-red-800/50">
          <div 
            className="p-6 border-b border-red-100 dark:border-red-800/30 cursor-pointer hover:bg-red-50/50 dark:hover:bg-red-900/10 transition-colors rounded-t-2xl"
            onClick={() => setShowFailed(!showFailed)}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-xl">
                  <XCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{t('pages.sync.failedEntries')}</h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {failedItems.length === 1
                      ? t('pages.sync.failedSummary', { count: failedItems.length })
                      : t('pages.sync.failedSummaryPlural', { count: failedItems.length })}
                  </p>
                </div>
              </div>
              {showFailed ? (
                <ChevronUp className="w-5 h-5 text-gray-400" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-400" />
              )}
            </div>
          </div>

          {showFailed && (
            <div className="divide-y divide-gray-100 dark:divide-gray-700 max-h-[600px] overflow-y-auto">
              {failedLoading ? (
                <div className="p-8 text-center">
                  <RefreshCw className="w-8 h-8 text-gray-300 animate-spin mx-auto" />
                  <p className="mt-2 text-sm text-gray-500">{t('pages.sync.loadingFailed')}</p>
                </div>
              ) : failedItems.length === 0 ? (
                <div className="p-8 text-center text-gray-400">
                  <CheckCircle className="w-10 h-10 mx-auto mb-2 text-green-400" />
                  <p>{t('pages.sync.noFailed')}</p>
                </div>
              ) : (
                failedItems.map((item) => (
                  <div key={item.id} className="p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        {/* Type badge + Label */}
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                            item.type === 'TimeEntry' 
                              ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
                              : 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400'
                          }`}>
                            {item.type === 'TimeEntry' ? t('pages.sync.typeTimeEntry') : t('pages.sync.typeActivity')}
                          </span>
                          {item.productionTaskId && (
                            <span className="text-xs text-gray-400">{t('pages.sync.taskPrefix')} {item.productionTaskId}</span>
                          )}
                        </div>

                        {/* Label */}
                        <p className="font-medium text-gray-900 dark:text-gray-100 text-sm">
                          {item.label || t('pages.sync.unknownEntry')}
                        </p>

                        {/* Details */}
                        {item.details && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                            {item.details}
                          </p>
                        )}

                        {/* Error message — THE KEY PART */}
                        <div className="mt-2 p-2.5 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/40 rounded-lg">
                          <p className="text-xs font-medium text-red-700 dark:text-red-400 mb-0.5">{t('pages.sync.errorReason')}</p>
                          <p className="text-sm text-red-600 dark:text-red-300 font-mono break-all">
                            {item.errorMessage || t('pages.sync.noErrorDetail')}
                          </p>
                        </div>

                        {/* Failed timestamp */}
                        {item.failedAt && (
                          <p className="text-xs text-gray-400 mt-1.5">
                            {t('pages.sync.failedAt')} {parseUTC(item.failedAt).toLocaleString()}
                          </p>
                        )}
                      </div>

                      {/* Actions */}
                      <div className="flex flex-col gap-1.5 flex-shrink-0">
                        <button
                          onClick={() => handleRetrySingle(item.id, item.type)}
                          disabled={retryingId === item.id}
                          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-orange-600 dark:text-orange-400 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800/40 rounded-lg hover:bg-orange-100 dark:hover:bg-orange-900/40 transition-colors disabled:opacity-50"
                          title={t('pages.sync.retryTitle')}
                        >
                          <RotateCcw className={`w-3.5 h-3.5 ${retryingId === item.id ? 'animate-spin' : ''}`} />
                          {retryingId === item.id ? t('pages.sync.retrying') : t('pages.sync.retry')}
                        </button>
                        <button
                          onClick={() => handleDeleteFailed(item.id, item.type)}
                          disabled={deletingId === item.id}
                          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/40 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/40 transition-colors disabled:opacity-50"
                          title={t('pages.sync.deleteTitle')}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                          {deletingId === item.id ? t('pages.sync.deleting') : t('pages.sync.delete')}
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>

      {/* Recent Sync Activity */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700">
        <div className="p-6 border-b border-gray-100 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <History className="w-5 h-5 text-gray-500 dark:text-gray-400" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{t('pages.sync.recentActivity')}</h2>
          </div>
        </div>
        <div className="divide-y divide-gray-100 dark:divide-gray-700">
          {(Array.isArray(syncHistory) && syncHistory.length > 0 ? syncHistory : []).map((item, index) => {
            const isSuccess = item.status === 'Success' || item.status === 'success';
            const timeAgo = item.timestamp ? formatSyncTimeAgo(item.timestamp) : '';
            return (
              <div key={item.id || index} className="p-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700/50">
                <div className="flex items-center gap-4">
                  <div className={`p-2 rounded-lg ${isSuccess ? 'bg-green-100 dark:bg-green-900/30' : 'bg-yellow-100 dark:bg-yellow-900/30'}`}>
                    {isSuccess ? (
                      <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400" />
                    ) : (
                      <AlertTriangle className="w-4 h-4 text-yellow-600 dark:text-yellow-400" />
                    )}
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 dark:text-gray-100">{item.action || item.entityType}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">{timeAgo}</p>
                  </div>
                </div>
                <span className={`text-sm font-medium px-2.5 py-0.5 rounded-full ${
                  isSuccess ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' : 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400'
                }`}>
                  {item.status}
                </span>
              </div>
            );
          })}
          {(!Array.isArray(syncHistory) || syncHistory.length === 0) && (
            <div className="p-8 text-center text-gray-400 dark:text-gray-500">
              {t('pages.sync.noActivity')}
            </div>
          )}
        </div>
      </div>
      </PageFadeSection>
    </div>
  );
};

export default SyncPage;
